<template>
  <div class="content">
    <div class="rtcs-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-rtcs">

          <header class="glad-rtcs__header">
            <p class="glad-eyebrow">NTC Admin View</p>
            <h1 class="glad-rtcs__title">Regional Training Centers</h1>
            <p class="glad-rtcs__sub">Every RTC in the Project GLAD&reg; network, with membership at a glance.</p>
          </header>

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner"></span>
          </div>

          <div v-else-if="error" class="glad-error">
            {{ error }}
          </div>

          <template v-else>
            <!-- KPI cards -->
            <section class="glad-kpi-grid">
              <article class="glad-kpi">
                <p class="glad-kpi__label">Regional Training Centers</p>
                <p class="glad-kpi__value">{{ totalRtcs }}</p>
              </article>
              <article class="glad-kpi">
                <p class="glad-kpi__label">Total members</p>
                <p class="glad-kpi__value">{{ totalMembers }}</p>
              </article>
              <article class="glad-kpi">
                <p class="glad-kpi__label">Active learners</p>
                <p class="glad-kpi__value">{{ totalActive }}</p>
              </article>
            </section>

            <!-- RTC table -->
            <section class="glad-section">
              <header class="glad-section__head">
                <h2 class="glad-section__heading">All centers</h2>
                <span class="glad-section__hint">Membership counts by role</span>
              </header>

              <div v-if="!rtcs.length" class="glad-empty">
                No RTCs found.
              </div>

              <div v-else class="glad-table-wrap">
                <table class="glad-table">
                  <thead>
                    <tr>
                      <th scope="col">RTC</th>
                      <th scope="col" class="glad-table__num">Members</th>
                      <th scope="col" class="glad-table__num">Educators</th>
                      <th scope="col" class="glad-table__num">Trainers</th>
                      <th scope="col" class="glad-table__num">Admins</th>
                      <th scope="col" class="glad-table__num">Active</th>
                      <th scope="col" class="glad-table__actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="rtc in rtcs" :key="rtc.id">
                      <td>
                        <span class="glad-rtc-name">{{ rtc.name }}</span>
                        <span v-if="rtc.region" class="glad-rtc-region">{{ rtc.region }}</span>
                        <span v-if="rtc.is_ntc" class="glad-rtc-ntc">NTC</span>
                      </td>
                      <td class="glad-table__num">{{ rtc.members }}</td>
                      <td class="glad-table__num">{{ rtc.educators }}</td>
                      <td class="glad-table__num">{{ rtc.trainers }}</td>
                      <td class="glad-table__num">{{ rtc.admins }}</td>
                      <td class="glad-table__num">{{ rtc.active }}</td>
                      <td class="glad-table__actions">
                        <button
                          type="button"
                          class="glad-roster-btn"
                          @click="viewRoster(rtc)">
                          View roster
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </template>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AllRtcs',
  data() {
    return {
      loading: true,
      error: '',
      rtcs: [],
    };
  },
  computed: {
    totalRtcs() {
      return this.rtcs.length;
    },
    totalMembers() {
      return this.rtcs.reduce((sum, r) => sum + (Number(r.members) || 0), 0);
    },
    totalActive() {
      return this.rtcs.reduce((sum, r) => sum + (Number(r.active) || 0), 0);
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true;
      this.error = '';
      this.$http.get('glad/admin/rtcs')
        .then(resp => {
          this.rtcs = (resp.data && resp.data.data) ? resp.data.data : [];
        })
        .catch(err => {
          if (err && err.response && err.response.status === 403) {
            this.error = 'The RTC directory is available to NTC Admins only.';
          } else {
            this.error = 'Could not load RTCs.';
          }
        })
        .finally(() => { this.loading = false; });
    },
    viewRoster(rtc) {
      this.$router.push('/glad/rtcs/' + rtc.id + '/members');
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.rtcs-wrap { width: 100%; }
.rtcs-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-rtcs {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Header ------------------------------------------------------------------- */
.glad-rtcs__header { margin-bottom: 1.75rem; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-rtcs__title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-rtcs__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* Loading + error ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-error {
  padding: 2.5rem 1rem;
  text-align: center;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}

/* KPI grid ----------------------------------------------------------------- */
.glad-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-bottom: 2.25rem;
}
.glad-kpi {
  padding: 1.1rem 1.15rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
}
.glad-kpi__label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--glad-text-soft);
  margin: 0 0 0.4rem;
}
.glad-kpi__value {
  font-family: var(--glad-font-serif);
  font-size: 1.85rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
  line-height: 1;
}

/* Sections shared layout --------------------------------------------------- */
.glad-section { margin-bottom: 2.25rem; }
.glad-section:last-child { margin-bottom: 0; }

.glad-section__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}
.glad-section__heading {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
}
.glad-section__hint {
  font-size: 0.78rem;
  color: var(--glad-text-muted);
}

.glad-empty {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  text-align: center;
  padding: 1.5rem 0;
}

/* Table -------------------------------------------------------------------- */
.glad-table-wrap {
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  overflow: hidden;
}
.glad-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid var(--glad-line);
  }
  tr:last-child td { border-bottom: 0; }

  thead th {
    background: var(--glad-paper);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--glad-text-soft);
  }

  &__num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  thead .glad-table__num { text-align: right; }

  &__actions {
    text-align: right;
    white-space: nowrap;
  }
  thead .glad-table__actions { text-align: right; }
}
.glad-rtc-name { font-weight: 600; color: var(--glad-ink); }
.glad-rtc-region {
  display: inline-block;
  margin-left: 0.45rem;
  font-size: 0.72rem;
  color: var(--glad-text-muted);
}
.glad-rtc-ntc {
  display: inline-block;
  margin-left: 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 0.1rem 0.4rem;
  background: rgba(15, 17, 21, 0.08);
  color: var(--glad-ink);
  border-radius: 999px;
}

/* Roster button ------------------------------------------------------------ */
.glad-roster-btn {
  font-family: var(--glad-font-sans);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--glad-ink);
  background: transparent;
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--glad-paper-2);
    border-color: var(--glad-text-muted);
  }
  &:focus-visible {
    outline: 2px solid var(--glad-accent);
    outline-offset: 1px;
  }
}
</style>
